import { FC, useState } from 'react'
import { FocusedInputShape, DayPickerRangeController } from 'react-dates'
import { FaChevronLeft, FaChevronRight } from 'react-icons/fa6'
import moment, { Moment } from 'moment'
import styles from './styles.module.css'

moment.locale('ja')

const CalendarWeekHeader = {
  Su: {
    en: 'Sun',
    ja: '日',
  },
  Mo: {
    en: 'Mon',
    ja: '月',
  },
  Tu: {
    en: 'Tue',
    ja: '火',
  },
  We: {
    en: 'Wed',
    ja: '水',
  },
  Th: {
    en: 'Thu',
    ja: '木',
  },
  Fr: {
    en: 'Fri',
    ja: '金',
  },
  Sa: {
    en: 'Sat',
    ja: '土',
  },
}

type Props = {
  locale: string
  isOpen: boolean
  selectedDate: {
    startDate: moment.Moment | null
    endDate: moment.Moment | null
  }
  bookingStatus: any[]
  maxDate?: Moment
  setSelectedDate: ({ startDate, endDate }) => void
  onChangeMonth?: (momentObj: Moment) => void
}

export const Calendar: FC<Props> = ({
  locale,
  isOpen,
  selectedDate,
  maxDate = moment().add(1, 'month').endOf('month'),
  setSelectedDate,
  onChangeMonth,
}: Props): JSX.Element => {
  const [focused, setFocused] = useState<FocusedInputShape>('startDate')

  if (!isOpen) return null

  return (
    <DayPickerRangeController
      startDate={selectedDate.startDate}
      endDate={selectedDate.endDate}
      focusedInput={focused}
      onFocusChange={(focusedInput) => setFocused(!focusedInput ? 'startDate' : focusedInput)}
      onDatesChange={(values) => {
        if (focused === 'startDate') {
          setSelectedDate({
            startDate: values.startDate,
            endDate: values.startDate,
          })
        }

        if (focused === 'endDate') {
          if (!values.endDate) {
            setSelectedDate({
              startDate: values.startDate,
              endDate: values.startDate,
            })
            return
          }

          setSelectedDate({
            startDate: values.startDate,
            endDate: values.endDate,
          })
        }
      }}
      minDate={moment()}
      maxDate={maxDate}
      monthFormat={locale === 'ja' ? 'YYYY[年]MM[月]' : 'YYYY/MM'}
      numberOfMonths={1}
      hideKeyboardShortcutsPanel
      initialVisibleMonth={() => (selectedDate?.startDate ? selectedDate.startDate : moment())}
      keepOpenOnDateSelect={false}
      isOutsideRange={(day) => day.isBefore(moment(), 'day') || day.isAfter(maxDate, 'day')}
      navPrev={
        <div className={styles.prevButton}>
          <FaChevronLeft size={20} color="#333" aria-hidden="true" />
        </div>
      }
      navNext={
        <div className={styles.nextButton}>
          <FaChevronRight size={20} color="#333" aria-hidden="true" />
        </div>
      }
      onNextMonthClick={onChangeMonth}
      onPrevMonthClick={onChangeMonth}
      renderWeekHeaderElement={(day) => {
        return <div className={styles.weekHeader}>{CalendarWeekHeader?.[day]?.[locale]}</div>
      }}
      renderDayContents={(date) => {
        const isPastDate = date.isBefore(moment(), 'day')

        let symbolUrl = '/date_symbol_triangle.png'

        if (isPastDate) {
          symbolUrl = '/minus_symbol.png'
        }

        return (
          <div
            onClick={(event) => {
              if (['/date_symbol_x_gray.png', '/date_symbol_x.png'].includes(symbolUrl)) {
                event.stopPropagation()
              }
            }}
            className={styles.dateWithSymbolContainer}
          >
            {date.format('DD')}
            {symbolUrl && (
              <img src={symbolUrl} alt="" className={styles.symbol} height={12} width={12} />
            )}
          </div>
        )
      }}
    />
  )
}
